import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StatusBadge } from '@shared/components/ui/UI';
import { CameraIcon, CameraOffIcon, MicrophoneIcon, MicrophoneOffIcon } from '@shared/components/icons';

// Video Preview Component
export const VideoPreview = ({
  stream,
  muted = true,
  mirrored = true,
  className = '',
  showStatus = true,
  isActive = true,
  label,
  showControls = false,
  onToggleCamera,
  onToggleMicrophone,
  cameraEnabled = true,
  microphoneEnabled = true,
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      console.log('VideoPreview: Setting stream', {
        hasStream: !!stream,
        streamActive: stream.active,
        videoTracks: stream.getVideoTracks().length,
        trackState: stream.getVideoTracks()[0]?.readyState,
        trackEnabled: stream.getVideoTracks()[0]?.enabled,
        isActive,
        cameraEnabled
      });
      videoRef.current.srcObject = stream;

      // Force play when stream is set and camera is enabled
      if (isActive && cameraEnabled) {
        videoRef.current.play().catch(err => {
          console.log('Video play failed:', err);
        });
      }
    } else if (videoRef.current && !stream) {
      // Clear srcObject when stream is removed
      console.log('VideoPreview: Clearing srcObject (stream removed)');
      videoRef.current.srcObject = null;
    }

    // Cleanup: clear srcObject when effect re-runs or component unmounts
    return () => {
      if (videoRef.current) {
        console.log('VideoPreview: Cleanup - clearing srcObject');
        videoRef.current.srcObject = null;
      }
    };
  }, [stream, isActive, cameraEnabled]);

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gray-900 ${className}`}>
      {stream ? (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={muted}
            className={`w-full h-full object-cover ${mirrored ? 'scale-x-[-1]' : ''} ${!isActive || !cameraEnabled ? 'hidden' : ''}`}
          />
          {(!isActive || !cameraEnabled) && (
            <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <div className="text-center">
                <CameraOffIcon size={48} className="mx-auto text-gray-500 mb-2" />
                <p className="text-gray-400 text-sm">Camera Off</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <div className="text-center">
            <CameraOffIcon size={48} className="mx-auto text-gray-500 mb-2" />
            <p className="text-gray-400 text-sm">Camera Off</p>
          </div>
        </div>
      )}
      
      {/* Overlay gradient */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
      
      {/* Status indicator */}
      {showStatus && (
        <div className="absolute bottom-4 start-4">
          <StatusBadge status={isActive ? 'active' : 'inactive'}>
            {isActive ? 'Camera Active' : 'Camera Off'}
          </StatusBadge>
        </div>
      )}
      
      {/* Label */}
      {label && (
        <div className="absolute top-4 start-4">
          <span className="px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm font-medium">
            {label}
          </span>
        </div>
      )}
      
      {/* Controls - Left side */}
      {showControls && (
        <div className="absolute bottom-4 start-4 flex gap-2">
          <button
            onClick={onToggleCamera}
            className={`
              p-3 rounded-full backdrop-blur-sm transition-all
              ${cameraEnabled
                ? 'bg-gray-800/80 text-white hover:bg-gray-700/80'
                : 'bg-red-500/80 text-white hover:bg-red-600/80'
              }
            `}
          >
            {cameraEnabled ? <CameraIcon size={20} /> : <CameraOffIcon size={20} />}
          </button>
          <button
            onClick={onToggleMicrophone}
            className={`
              p-3 rounded-full backdrop-blur-sm transition-all
              ${microphoneEnabled
                ? 'bg-gray-800/80 text-white hover:bg-gray-700/80'
                : 'bg-red-500/80 text-white hover:bg-red-600/80'
              }
            `}
          >
            {microphoneEnabled ? <MicrophoneIcon size={20} /> : <MicrophoneOffIcon size={20} />}
          </button>
        </div>
      )}

      {/* Status indicator dot only - Right side */}
      {showControls && (
        <div className="absolute bottom-4 end-4 flex items-center gap-2 px-2 py-2 bg-gray-800/80 backdrop-blur-sm rounded-full">
          {cameraEnabled && stream ? (
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          ) : (
            <div className="w-2 h-2 bg-amber-500 rounded-full" />
          )}
        </div>
      )}
    </div>
  );
};

// AI Avatar Component
export const AIAvatar = ({ 
  name = 'Sarah', 
  isActive = true, 
  isSpeaking = false,
  className = '' 
}) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl ${className}`}>
      {/* Background with tech pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950">
        {/* Animated circuit lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 400">
          <defs>
            <linearGradient id="circuitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#818cf8" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <g stroke="url(#circuitGrad)" strokeWidth="1" fill="none">
            <path d="M0,100 H150 L170,120 H250" className="animate-pulse" />
            <path d="M400,150 H300 L280,170 H200" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
            <path d="M0,250 H100 L120,230 H180" className="animate-pulse" style={{ animationDelay: '1s' }} />
            <path d="M400,300 H350 L330,280 H270" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
            <circle cx="150" cy="100" r="4" fill="#60a5fa" className="animate-pulse" />
            <circle cx="300" cy="150" r="4" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
            <circle cx="100" cy="250" r="4" fill="#60a5fa" className="animate-pulse" style={{ animationDelay: '1s' }} />
          </g>
        </svg>
      </div>
      
      {/* Avatar Image Area */}
      <div className="relative w-full h-full flex items-center justify-center">
        {/* AI Avatar placeholder - You would replace this with actual avatar */}
        <div className="relative">
          {/* Glow effect when speaking */}
          {isSpeaking && (
            <motion.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="absolute inset-0 bg-primary-500/30 rounded-full blur-xl"
            />
          )}
          
          {/* Avatar container */}
          <div className="w-48 h-48 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center overflow-hidden border-4 border-slate-600/50">
            {/* Placeholder avatar - replace with actual image */}
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="avatarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1e293b" />
                  <stop offset="100%" stopColor="#0f172a" />
                </linearGradient>
              </defs>
              <circle cx="100" cy="100" r="100" fill="url(#avatarGrad)" />
              <ellipse cx="100" cy="85" rx="35" ry="40" fill="#475569" />
              <circle cx="100" cy="160" r="50" fill="#475569" />
            </svg>
          </div>
          
          {/* Speaking indicator */}
          {isSpeaking && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ height: ['8px', '16px', '8px'] }}
                  transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                  className="w-1 bg-primary-400 rounded-full"
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Name label */}
      <div className="absolute bottom-4 start-4">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-blue-400 animate-pulse' : 'bg-gray-500'}`} />
          <span className="text-white font-medium">{name} (AI Interviewer)</span>
        </div>
      </div>
    </div>
  );
};

// Interview Video Grid
export const InterviewVideoGrid = ({
  userStream,
  userEnabled,
  aiName = 'Sarah',
  aiSpeaking = false,
  layout = 'side-by-side', // 'side-by-side' | 'main-pip' | 'pip-main'
  className = '',
}) => {
  if (layout === 'side-by-side') {
    return (
      <div className={`grid grid-cols-2 gap-6 ${className}`}>
        <AIAvatar 
          name={aiName} 
          isActive={true} 
          isSpeaking={aiSpeaking}
          className="aspect-[4/3]"
        />
        <VideoPreview
          stream={userStream}
          isActive={userEnabled}
          showStatus={true}
          className="aspect-[4/3]"
        />
      </div>
    );
  }
  
  if (layout === 'main-pip') {
    return (
      <div className={`relative ${className}`}>
        <AIAvatar 
          name={aiName} 
          isActive={true} 
          isSpeaking={aiSpeaking}
          className="w-full aspect-video"
        />
        <div className="absolute top-4 end-4 w-48 aspect-video shadow-strong rounded-xl overflow-hidden">
          <VideoPreview
            stream={userStream}
            isActive={userEnabled}
            showStatus={false}
            label="You"
          />
        </div>
      </div>
    );
  }
  
  // pip-main layout
  return (
    <div className={`relative ${className}`}>
      <VideoPreview
        stream={userStream}
        isActive={userEnabled}
        showStatus={true}
        className="w-full aspect-video"
      />
      <div className="absolute top-4 start-4 w-48 aspect-video shadow-strong rounded-xl overflow-hidden">
        <AIAvatar 
          name={aiName} 
          isActive={true} 
          isSpeaking={aiSpeaking}
        />
      </div>
    </div>
  );
};

// Recording Indicator
export const RecordingIndicator = ({ isRecording, duration }) => {
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isRecording) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 rounded-full">
      <span className="w-2 h-2 bg-red-500 rounded-full recording-indicator" />
      <span className="text-red-500 text-sm font-medium">
        REC {formatDuration(duration)}
      </span>
    </div>
  );
};

export default VideoPreview;
